import { Monitor, Moon, Palette, Sun, type LucideProps } from 'lucide-react'
import type { ComponentType } from 'react'

import { useUpdateSettings } from '@/api/hooks'
import type { Theme } from '@/api/types'
import { cn } from '@/lib/cn'
import { useTheme } from '@/lib/theme'

import { SettingsSection } from './SettingsSection'

interface ThemeOption {
  value: Theme
  label: string
  icon: ComponentType<LucideProps>
}

const OPTIONS: readonly ThemeOption[] = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
]

export function AppearanceSection() {
  const { theme, setTheme } = useTheme()
  const update = useUpdateSettings()

  function choose(next: Theme) {
    if (next !== theme) update.reset()
    // Applied locally first: the preference is stored on this device and read
    // before first paint, so the switch must never wait on the network.
    setTheme(next)
    update.mutate({ theme: next })
  }

  return (
    <SettingsSection
      icon={Palette}
      title="Appearance"
    >
      <fieldset>
        <legend className="sr-only">Theme</legend>

        <div className="grid grid-cols-3 gap-1 rounded-lg bg-surface-sunken p-1">
          {OPTIONS.map(({ value, label, icon: Icon }) => {
            const selected = theme === value
            return (
              <label
                key={value}
                className={cn(
                  'flex min-h-11 cursor-pointer select-none flex-col items-center justify-center gap-1 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                  'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
                  selected
                    ? 'bg-surface-raised text-text shadow-sm ring-1 ring-border'
                    : 'text-text-muted hover:bg-surface-hover hover:text-text',
                )}
              >
                <input
                  type="radio"
                  name="tunes-theme"
                  value={value}
                  checked={selected}
                  onChange={() => choose(value)}
                  className="sr-only"
                />
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </label>
            )
          })}
        </div>
      </fieldset>

      {update.isError ? (
        <p className="mt-3 text-sm text-text-muted">
          Saved on this device. The server did not record the change, so another device may still
          look different.
        </p>
      ) : null}
    </SettingsSection>
  )
}
